import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HomeOutlined,
  PlusOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import SEO from './SEO';
import {
  notificationTimeout,
  notificationType,
} from '../common/constants';
import notValid from '../common/notValid';
import {
  firAddDoorChannel,
  firAssignSuperAdmin,
  firCreateSite,
  firDeleteSite,
  firGetDoorChannels,
  firListAllSites,
  firRemoveDoorChannel,
  firSetSiteActive,
  firUpdateDoorChannel,
  firUpdateSite,
} from '../firebase';
import useAuthClaims from '../common/useAuthClaims';
import './style.scss';

const { Text } = Typography;

// D24 rev 77 (2026-05-15) — All Sites tab. Visible only to users with
// the `root: true` Auth custom claim (ATACT staff, accessLevel 1-10).
// Lets staff create new sites, edit basic config, and manage door
// channels (the install-only path admins can't access).
//
// Security note: the gate is frontend-only for MVP. Firestore rules
// still permit any authenticated user to create site docs because
// the project's wildcard rule (`{document=**} allow read, write: if
// request.auth.uid != null`) overrides any narrower restriction.
// Tightening is tracked in firestore.rules.target which lands as part
// of the broader rules-hardening pass blocked on VMS team migration.
export default function ManageSites() {
  const claims = useAuthClaims();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);

  // ---- Create / Edit site modal ----
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null); // null = create
  const [siteForm] = Form.useForm();
  const [siteSaving, setSiteSaving] = useState(false);

  // ---- Door channels modal ----
  const [doorModalOpen, setDoorModalOpen] = useState(false);
  const [doorTargetSite, setDoorTargetSite] = useState(null);
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelEditing, setChannelEditing] = useState(null);
  const [channelForm] = Form.useForm();
  const [channelSaving, setChannelSaving] = useState(false);
  const [channelFormOpen, setChannelFormOpen] = useState(false);

  // ---- Assign SuperAdmin modal ----
  const [saModalOpen, setSaModalOpen] = useState(false);
  const [saTargetSite, setSaTargetSite] = useState(null);
  const [saForm] = Form.useForm();
  const [saSaving, setSaSaving] = useState(false);

  // rev 95: per-site Version Gate state removed. See MobileVersionGate
  // tab for the global gate that replaced it.

  // ---- Delete Site modal (rev 105, ROADMAP §2.6) ----
  // Root-only destructive flow. Two-stage type-to-confirm (UI requires
  // typing the phrase; BE re-validates via confirmAck param).
  const [delModalOpen, setDelModalOpen] = useState(false);
  const [delTargetSite, setDelTargetSite] = useState(null);
  const [delConfirmPhrase, setDelConfirmPhrase] = useState('');
  const [delDeleting, setDelDeleting] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();
  const { timeoutError, timeoutSuccess } = notificationTimeout;
  const { typeError, typeSuccess } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => messageApi.open({ content, duration, type }),
    [messageApi],
  );

  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await firListAllSites();
      setSites(rows);
    } catch (e) {
      console.error('-- Error listing sites:', e);
      openMessage('Error listing sites', timeoutError, typeError);
    } finally {
      setLoading(false);
    }
  }, [openMessage, timeoutError, typeError]);

  useEffect(() => {
    if (claims.ready && claims.root) {
      fetchSites();
    }
  }, [claims.ready, claims.root, fetchSites]);

  // ---------- Site create / edit ----------
  //
  // Note (2026-05-15): The Modal uses `destroyOnClose` so Form fields are
  // unmounted between opens. Calling setFieldsValue inline in
  // openCreateSite / openEditSite runs BEFORE the fields remount on the
  // next open, so the values are dropped. We instead set state here +
  // populate via the Modal's `afterOpenChange={open => open && ...}` hook,
  // which fires after the fields have remounted.

  const openCreateSite = () => {
    setEditingSite(null);
    setSiteModalOpen(true);
  };

  const openEditSite = (row) => {
    setEditingSite(row);
    setSiteModalOpen(true);
  };

  const populateSiteForm = useCallback(() => {
    siteForm.resetFields();
    if (editingSite) {
      siteForm.setFieldsValue({
        displayName: editingSite.displayName,
        slug: editingSite.slug,
        address: editingSite.address,
        active: editingSite.active,
        siteType: editingSite.siteType,
        householdTenantLimit: editingSite.config?.householdTenantLimit ?? 10,
        kioskButtonCooldown: editingSite.config?.kioskButtonCooldown ?? 60,
        kioskNotificationGracePeriod: editingSite.config?.kioskNotificationGracePeriod ?? 60,
        tenantAccountApprove: editingSite.config?.tenantAccountApprove ?? false,
        useTwilioNotification: editingSite.config?.useTwilioNotification ?? true,
        visitorMaxDays: editingSite.config?.visitorMaxDays ?? 7,
      });
    } else {
      siteForm.setFieldsValue({
        active: false,
        siteType: '',
        householdTenantLimit: 10,
        kioskButtonCooldown: 60,
        kioskNotificationGracePeriod: 60,
        tenantAccountApprove: false,
        useTwilioNotification: true,
        visitorMaxDays: 7,
      });
    }
  }, [editingSite, siteForm]);

  const handleSiteOk = async () => {
    try {
      const values = await siteForm.validateFields();
      setSiteSaving(true);
      if (editingSite) {
        // Slug is immutable post-creation — intentionally NOT in the patch
        // even though it appears (disabled) in the form.
        await firUpdateSite(editingSite.id, {
          displayName: values.displayName.trim(),
          active: !!values.active,
          'information.address': (values.address || '').trim(),
          'information.siteType': (values.siteType || '').trim(),
          'configuration.householdTenantLimit': values.householdTenantLimit ?? 10,
          'configuration.kioskButtonCooldown': values.kioskButtonCooldown ?? 60,
          'configuration.kioskNotificationGracePeriod': values.kioskNotificationGracePeriod ?? 60,
          'configuration.tenantAccountApprove': !!values.tenantAccountApprove,
          'configuration.useTwilioNotification': !!values.useTwilioNotification,
          'configuration.visitorMaxDays': values.visitorMaxDays ?? 7,
        });
        openMessage('Site updated', timeoutSuccess, typeSuccess);
      } else {
        const slug = values.slug.trim().toLowerCase();
        // Client-side uniqueness check. Not a security guarantee (Firestore
        // rules + a server-side check would be), but catches the common
        // typo path before we write.
        if (sites.some((s) => s.slug && s.slug.toLowerCase() === slug)) {
          openMessage(`Slug "${slug}" is already used by another site`, timeoutError, typeError);
          setSiteSaving(false);
          return;
        }
        await firCreateSite({
          displayName: values.displayName.trim(),
          slug,
          address: (values.address || '').trim(),
          siteType: (values.siteType || '').trim(),
          active: !!values.active,
          householdTenantLimit: values.householdTenantLimit ?? 10,
          kioskButtonCooldown: values.kioskButtonCooldown ?? 60,
          kioskNotificationGracePeriod: values.kioskNotificationGracePeriod ?? 60,
          tenantAccountApprove: !!values.tenantAccountApprove,
          useTwilioNotification: !!values.useTwilioNotification,
          visitorMaxDays: values.visitorMaxDays ?? 7,
        });
        openMessage('Site created', timeoutSuccess, typeSuccess);
      }
      setSiteModalOpen(false);
      siteForm.resetFields();
      setEditingSite(null);
      fetchSites();
    } catch (e) {
      if (e.errorFields) return;
      console.error('-- Error saving site:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    } finally {
      setSiteSaving(false);
    }
  };

  const handleActiveToggle = async (row, next) => {
    try {
      await firSetSiteActive(row.id, next);
      openMessage(next ? 'Site activated' : 'Site deactivated', timeoutSuccess, typeSuccess);
      fetchSites();
    } catch (e) {
      console.error('-- Error toggling active:', e);
      openMessage('Error updating site', timeoutError, typeError);
    }
  };

  // ---------- Door channels (per-site sub-modal) ----------

  const loadChannels = useCallback(
    async (siteId) => {
      if (notValid(siteId)) return;
      setChannelsLoading(true);
      try {
        const map = await firGetDoorChannels(siteId);
        const list = Object.entries(map)
          .map(([code, f]) => ({
            key: code,
            channelCode: code,
            label: f?.label || '',
            isVisitorEntry: f?.isVisitorEntry === true,
          }))
          .sort((a, b) =>
            a.channelCode.localeCompare(b.channelCode, undefined, { numeric: true }),
          );
        setChannels(list);
      } catch (e) {
        console.error('-- Error fetching channels:', e);
        openMessage('Error fetching channels', timeoutError, typeError);
      } finally {
        setChannelsLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  const openDoorChannels = async (row) => {
    setDoorTargetSite(row);
    setChannels([]);
    setChannelEditing(null);
    setChannelFormOpen(false);
    setDoorModalOpen(true);
    await loadChannels(row.id);
  };

  const openAddChannel = () => {
    setChannelEditing(null);
    setChannelFormOpen(true);
  };

  const openEditChannel = (record) => {
    setChannelEditing(record);
    setChannelFormOpen(true);
  };

  // Suggest the next numeric channel code for a new channel: max existing
  // numeric code + 1, or 101 if none exist. Non-numeric codes (e.g. "A1")
  // are ignored for the max calc; user can still hand-type any value.
  const nextChannelCodeSuggestion = useCallback(() => {
    const nums = channels
      .map((c) => Number(c.channelCode))
      .filter((n) => Number.isFinite(n));
    if (nums.length === 0) return '101';
    return String(Math.max(...nums) + 1);
  }, [channels]);

  // Populated after the nested channel modal's fields mount — same
  // destroyOnClose timing trap as the outer site modal.
  const populateChannelForm = useCallback(() => {
    channelForm.resetFields();
    if (channelEditing) {
      channelForm.setFieldsValue({
        channelCode: channelEditing.channelCode,
        label: channelEditing.label,
        isVisitorEntry: channelEditing.isVisitorEntry,
      });
    } else {
      // Pre-fill with the next numeric code; user can still override.
      channelForm.setFieldsValue({
        channelCode: nextChannelCodeSuggestion(),
        isVisitorEntry: true,
      });
    }
  }, [channelEditing, channelForm, nextChannelCodeSuggestion]);

  const handleChannelOk = async () => {
    if (!doorTargetSite) return;
    try {
      const values = await channelForm.validateFields();
      const code = String(values.channelCode).trim();
      setChannelSaving(true);
      if (channelEditing) {
        await firUpdateDoorChannel(doorTargetSite.id, channelEditing.channelCode, {
          label: values.label.trim(),
          isVisitorEntry: !!values.isVisitorEntry,
        });
        openMessage('Channel updated', timeoutSuccess, typeSuccess);
      } else {
        // Add — guard against overwriting an existing code.
        if (channels.some((c) => c.channelCode === code)) {
          openMessage(`Channel ${code} already exists`, timeoutError, typeError);
          setChannelSaving(false);
          return;
        }
        await firAddDoorChannel(doorTargetSite.id, code, {
          label: values.label.trim(),
          isVisitorEntry: !!values.isVisitorEntry,
        });
        openMessage('Channel added', timeoutSuccess, typeSuccess);
      }
      setChannelFormOpen(false);
      channelForm.resetFields();
      setChannelEditing(null);
      await loadChannels(doorTargetSite.id);
      fetchSites(); // refresh channelCount in outer table
    } catch (e) {
      if (e.errorFields) return;
      console.error('-- Error saving channel:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    } finally {
      setChannelSaving(false);
    }
  };

  const handleChannelDelete = async (record) => {
    if (!doorTargetSite) return;
    try {
      await firRemoveDoorChannel(doorTargetSite.id, record.channelCode);
      openMessage(`Channel ${record.channelCode} removed`, timeoutSuccess, typeSuccess);
      await loadChannels(doorTargetSite.id);
      fetchSites();
    } catch (e) {
      console.error('-- Error removing channel:', e);
      openMessage('Error removing channel', timeoutError, typeError);
    }
  };

  // ---------- Assign SuperAdmin ----------

  const openAssignSuperAdmin = (row) => {
    setSaTargetSite(row);
    setSaModalOpen(true);
  };

  const populateSaForm = useCallback(() => {
    saForm.resetFields();
    // No pre-fill: assigning is always a fresh email/fullname/contact, even
    // when reassigning. Showing the current SuperAdmin's data could lead
    // staff to accidentally "Save" the same email + a typo in fullname.
  }, [saForm]);

  const handleSaOk = async () => {
    if (!saTargetSite) return;
    try {
      const values = await saForm.validateFields();
      const email = values.email.trim().toLowerCase();
      const fullname = values.fullname.trim();
      const contactNumber = (values.contactNumber || '').trim();

      // Confirm if site already has a SuperAdmin AND the email is different
      // (i.e. an actual reassignment, not just resending the invite to the
      // same person).
      const current = saTargetSite.superAdmin;
      if (current && current.email && current.email.toLowerCase() !== email) {
        const ok = await new Promise((resolve) => {
          Modal.confirm({
            title: 'Reassign SuperAdmin?',
            content: (
              <div>
                <p>
                  <strong>{current.email}</strong> is currently the SuperAdmin
                  for <strong>{saTargetSite.displayName}</strong>.
                </p>
                <p>
                  They will be demoted to <strong>Admin</strong> (accessLevel
                  12). Their account stays active and they keep admin claims,
                  but lose SuperAdmin privileges.
                </p>
                <p>Proceed with reassignment?</p>
              </div>
            ),
            okText: 'Reassign',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!ok) return;
      }

      setSaSaving(true);
      const result = await firAssignSuperAdmin(saTargetSite.id, email, fullname, contactNumber);
      const msg =
        result.action === 'promoted'
          ? `Existing user promoted to SuperAdmin. Invite email sent to ${email}.`
          : `SuperAdmin created. Invite email sent to ${email}.`;
      openMessage(msg, timeoutSuccess, typeSuccess);
      if (result.demotedEmail) {
        openMessage(
          `Previous SuperAdmin (${result.demotedEmail}) demoted to Admin`,
          timeoutSuccess,
          typeSuccess,
        );
      }
      setSaModalOpen(false);
      saForm.resetFields();
      setSaTargetSite(null);
      fetchSites();
    } catch (e) {
      if (e.errorFields) return;
      console.error('-- Error assigning SuperAdmin:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    } finally {
      setSaSaving(false);
    }
  };

  // rev 95: per-site Version Gate handlers + modal removed. The global
  // mobile gate now lives at config/global and is edited from the
  // MobileVersionGate route in the left nav.

  // ---------- Delete Site (rev 105, ROADMAP §2.6) ----------

  const openDeleteSite = (row) => {
    setDelTargetSite(row);
    setDelConfirmPhrase('');
    setDelModalOpen(true);
  };

  const delRequiredPhrase = delTargetSite
    ? `DELETE ${delTargetSite.displayName.toUpperCase()}`
    : '';

  const handleDeleteSite = async () => {
    if (!delTargetSite) return;
    if (delConfirmPhrase !== delRequiredPhrase) {
      openMessage('Confirm phrase does not match', timeoutError, typeError);
      return;
    }
    try {
      setDelDeleting(true);
      const result = await firDeleteSite(delTargetSite.id);
      openMessage(
        `Site deleted. ${result.accountsDeactivated} account(s) deactivated, ` +
        `${result.blocksRemoved} block(s) + ${result.unitsRemoved} unit(s) removed.`,
        timeoutSuccess,
        typeSuccess,
      );
      setDelModalOpen(false);
      setDelTargetSite(null);
      setDelConfirmPhrase('');
      fetchSites();
    } catch (e) {
      console.error('-- Error deleting site:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    } finally {
      setDelDeleting(false);
    }
  };

  // ---------- Render ----------

  // Loading the claim takes one render cycle.
  if (!claims.ready) {
    return (
      <>
        <SEO description="Manage all sites" name="qiwei" title="Manage Sites" type="webapp" />
        <Text type="secondary">Loading…</Text>
      </>
    );
  }
  if (!claims.root) {
    return (
      <>
        <SEO description="Manage all sites" name="qiwei" title="Manage Sites" type="webapp" />
        <Alert
          message="Staff only"
          description="This screen is only available to ATACT staff (root claim)."
          showIcon
          type="warning"
        />
      </>
    );
  }

  const columns = [
    {
      title: 'Display Name',
      dataIndex: 'displayName',
      sorter: (a, b) => a.displayName.localeCompare(b.displayName),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Site ID',
      dataIndex: 'id',
      width: 160,
      render: (val) => (
        <Text
          copyable={{ text: val }}
          style={{
            fontFamily: 'monospace',
            fontSize: 12,
            wordBreak: 'break-all',
            whiteSpace: 'normal',
          }}
        >
          {val}
        </Text>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      width: 130,
      render: (val) => (val ? <span className="mono">{val}</span> : <Text type="secondary">—</Text>),
    },
    {
      title: 'Type',
      dataIndex: 'siteType',
      width: 110,
      render: (val) => val
        ? <Tag color="geekblue">{val}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Address',
      dataIndex: 'address',
      // Wrap to multiple lines instead of single-line ellipsis so the full
      // address is visible on narrow screens.
      render: (val) =>
        val
          ? <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{val}</span>
          : <Text type="secondary">—</Text>,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      align: 'center',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (v, r) => r.active === v,
      render: (val, row) => (
        <Switch
          checked={val}
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
          onChange={(next) => handleActiveToggle(row, next)}
        />
      ),
    },
    {
      title: 'Channels',
      dataIndex: 'channelCount',
      width: 110,
      align: 'center',
      render: (val) =>
        val > 0 ? <Tag color="blue">{val}</Tag> : <Tag color="default">0</Tag>,
    },
    {
      title: 'SuperAdmin',
      dataIndex: 'superAdmin',
      width: 200,
      render: (val) =>
        val
          ? (
            <span style={{ wordBreak: 'break-all', whiteSpace: 'normal', fontSize: 12 }}>
              {val.fullname || val.email}
              {val.fullname && val.email
                ? <div style={{ color: '#888', fontSize: 11 }}>{val.email}</div>
                : null}
            </span>
          )
          : <Text type="secondary">Not assigned</Text>,
    },
    {
      title: 'Actions',
      width: 420,
      render: (_, row) => (
        <Space size="small" wrap>
          <Button
            icon={<EditOutlined />}
            onClick={() => openEditSite(row)}
            size="small"
            type="link"
          >
            Edit
          </Button>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={() => openDoorChannels(row)}
            size="small"
            type="link"
          >
            Doors
          </Button>
          <Button
            icon={<UserAddOutlined />}
            onClick={() => openAssignSuperAdmin(row)}
            size="small"
            type="link"
          >
            {row.superAdmin ? 'Reassign SA' : 'Assign SA'}
          </Button>
          {/* rev 95 (2026-05-17): per-site Version Gate button removed.
              Same App Store/Play Store binary serves all sites — the gate
              now lives at config/global and is editable from the new
              "Mobile Version Gate" tab in the left nav. */}
          {/* rev 106 — Open as Admin. Sets localStorage + reloads /home
              so home.jsx re-resolves site context to this row's site.
              Page already gates on claims.root. */}
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              localStorage.setItem('impersonatedSiteId', row.id);
              window.location.href = '/home';
            }}
            size="small"
            type="link"
          >
            Open as Admin
          </Button>
          {/* rev 105 — destructive. Inherits the page's claims.root gate. */}
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => openDeleteSite(row)}
            size="small"
            type="link"
          >
            Delete Site
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <SEO
        description="Manage all sites"
        name="qiwei"
        title="Manage Sites"
        type="webapp"
      />
      {contextHolder}
      <Breadcrumb
        items={[
          {
            title: (
              <Link to="..">
                <HomeOutlined className="dropdown-icon-margin" />
                <span>Home</span>
              </Link>
            ),
          },
          { title: <span>Manage Sites</span> },
        ]}
      />
      <Alert
        message="Staff-only — manage all sites in this Firebase project"
        description="Create new sites, toggle active status, and install door channels. Per-site configuration (blocks, residents, rejection reasons, etc.) is edited by each site's admin under Site Config."
        showIcon
        style={{ margin: '12px 0 16px' }}
        type="info"
      />
      <Flex justify="flex-end" style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} onClick={openCreateSite} type="primary">
          Create Site
        </Button>
      </Flex>
      <Table
        columns={columns}
        dataSource={sites}
        loading={loading}
        pagination={{
          showSizeChanger: true,
          position: ['bottomCenter'],
        }}
        rowKey="id"
      />

      {/* ----- Create / Edit Site modal ----- */}
      <Modal
        title={editingSite ? `Edit Site — ${editingSite.displayName}` : 'Create New Site'}
        open={siteModalOpen}
        onOk={handleSiteOk}
        confirmLoading={siteSaving}
        onCancel={() => {
          setSiteModalOpen(false);
          siteForm.resetFields();
          setEditingSite(null);
        }}
        okText={editingSite ? 'Save Changes' : 'Create Site'}
        destroyOnClose
        // Populate form values AFTER fields remount (destroyOnClose drops
        // them between opens). Calling setFieldsValue earlier silently
        // no-ops because the form has no fields yet.
        afterOpenChange={(open) => { if (open) populateSiteForm(); }}
        width={560}
      >
        <Form form={siteForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Display Name"
            name="displayName"
            rules={[{ required: true, message: 'Please enter a display name' }]}
            tooltip="Shown to admins + applicants. e.g. 'A TACT Office'."
          >
            <Input placeholder="e.g., A TACT Office" maxLength={80} />
          </Form.Item>
          <Form.Item
            label={editingSite ? 'Slug (cannot be changed)' : 'Slug'}
            name="slug"
            rules={editingSite ? [] : [
              { required: true, message: 'Please enter a slug' },
              {
                pattern: /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/,
                message: 'Lowercase letters, digits and dashes only (2–40 chars, no leading/trailing dash)',
              },
            ]}
            tooltip={
              editingSite
                ? 'Slug is set once at creation and cannot be changed afterwards. Used in URLs and integrations downstream — changing it would orphan external references.'
                : 'Short URL-friendly identifier. Set once at creation — cannot be changed later. Used in URLs and integrations downstream.'
            }
            extra={
              editingSite
                ? 'Slug is locked. To change it you would need to migrate all downstream references — contact engineering.'
                : 'Lowercase letters / digits / dashes. Cannot be changed once the site is created.'
            }
          >
            <Input
              disabled={!!editingSite}
              maxLength={40}
              placeholder="e.g., atact-office"
              style={{ fontFamily: editingSite ? 'monospace' : undefined }}
            />
          </Form.Item>
          <Form.Item
            label="Address"
            name="address"
            tooltip="Full address; appears in resident-facing screens."
          >
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 3 }}
              maxLength={250}
              placeholder="e.g., 2 Kallang Pudding Road #08-07 Mactech Building Singapore 349307"
              showCount
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Site Type"
                name="siteType"
                tooltip="e.g., Office, Residential, Mixed Use. Free text for now."
              >
                <Input placeholder="e.g., Office" maxLength={40} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Active"
                name="active"
                valuePropName="checked"
                tooltip="When on, the site shows up in the mobile account-application site picker. Keep OFF until door channels are installed and site config is complete."
              >
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>
            Configuration
          </Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Household tenant limit"
                name="householdTenantLimit"
                tooltip="Cap on total people per household (owners + tenants combined). Per-role breakdown is a future enhancement."
                rules={[{ type: 'number', min: 1, max: 100 }]}
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Visitor card valid for (days)"
                name="visitorMaxDays"
                tooltip="Pre-registered visitor cards expire after this many days."
                rules={[{ type: 'number', min: 1, max: 90 }]}
              >
                <InputNumber min={1} max={90} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Kiosk button cooldown (seconds)"
                name="kioskButtonCooldown"
                tooltip="Door-open button on the kiosk is disabled for this many seconds after a press, to prevent double-trigger."
                rules={[{ type: 'number', min: 0, max: 600 }]}
              >
                <InputNumber min={0} max={600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Kiosk notification grace period (seconds)"
                name="kioskNotificationGracePeriod"
                tooltip="How long the kiosk waits for the resident to respond to a visitor notification before timing out."
                rules={[{ type: 'number', min: 0, max: 600 }]}
              >
                <InputNumber min={0} max={600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Tenant account approval"
                name="tenantAccountApprove"
                valuePropName="checked"
                tooltip="When on, an owner must approve every tenant family-member addition before the tenant gets a Connect account."
              >
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Use Twilio SMS for visitor notifications"
                name="useTwilioNotification"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="More per-site config edited by the site admin"
            description={
              <span>
                Rejection reasons, application document types, and per-role
                household-limit breakdowns live under{' '}
                <span className="mono">Site Config → Account Application</span>.
                Door channels are managed in the <strong>Doors</strong> button
                on each row.
              </span>
            }
            showIcon
            style={{ marginTop: 8 }}
            type="info"
          />
        </Form>
      </Modal>

      {/* ----- Door Channels sub-modal ----- */}
      <Modal
        title={
          doorTargetSite
            ? (
              <span>
                Door Channels — <strong>{doorTargetSite.displayName}</strong>
              </span>
            )
            : 'Door Channels'
        }
        open={doorModalOpen}
        onCancel={() => {
          setDoorModalOpen(false);
          setDoorTargetSite(null);
          setChannelFormOpen(false);
          channelForm.resetFields();
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Alert
          message="Staff door-channel management"
          description="Channel codes (e.g. 101, 102) correspond to physical hardware. Adding / removing here implies an on-site install / decommission step. Admins on the per-site SiteConfig only see Edit (label + visitor-entry flag)."
          showIcon
          style={{ marginBottom: 12 }}
          type="info"
        />
        <Flex justify="flex-end" style={{ marginBottom: 12 }}>
          <Button icon={<PlusOutlined />} onClick={openAddChannel} type="primary" size="small">
            Add Channel
          </Button>
        </Flex>
        <Table
          columns={[
            {
              title: 'Channel',
              dataIndex: 'channelCode',
              width: 110,
              render: (val) => (
                <Text strong style={{ fontFamily: 'monospace' }}>{val}</Text>
              ),
            },
            {
              title: 'Label',
              dataIndex: 'label',
              render: (val) => val || <Text type="secondary">—</Text>,
            },
            {
              title: 'Visitor Entry',
              dataIndex: 'isVisitorEntry',
              width: 130,
              align: 'center',
              render: (val) =>
                val ? <Tag color="success">Yes</Tag> : <Tag color="default">No</Tag>,
            },
            {
              title: 'Actions',
              width: 170,
              render: (_, record) => (
                <Space size="small">
                  <Button
                    icon={<SettingOutlined />}
                    onClick={() => openEditChannel(record)}
                    size="small"
                    type="link"
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    cancelText="Cancel"
                    okText="Remove"
                    okType="danger"
                    onConfirm={() => handleChannelDelete(record)}
                    title={`Remove channel ${record.channelCode}?`}
                    description="This is destructive. Any unit or visit referencing this channel will break."
                  >
                    <Button danger icon={<DeleteOutlined />} size="small" type="link">
                      Remove
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          dataSource={channels}
          loading={channelsLoading}
          pagination={false}
          rowKey="key"
          size="small"
        />

        {/* Nested form modal for add/edit channel */}
        <Modal
          title={
            channelEditing
              ? `Edit Channel ${channelEditing.channelCode}`
              : 'Add New Channel'
          }
          open={channelFormOpen}
          onOk={handleChannelOk}
          confirmLoading={channelSaving}
          onCancel={() => {
            setChannelFormOpen(false);
            channelForm.resetFields();
            setChannelEditing(null);
          }}
          afterOpenChange={(open) => { if (open) populateChannelForm(); }}
          okText={channelEditing ? 'Save' : 'Add Channel'}
          destroyOnClose
        >
          <Form form={channelForm} layout="vertical" preserve={false}>
            <Form.Item
              label="Channel Code"
              name="channelCode"
              rules={[
                { required: true, message: 'Please enter a channel code' },
                {
                  pattern: /^[A-Za-z0-9_-]+$/,
                  message: 'Letters, digits, dash and underscore only',
                },
              ]}
              tooltip="Identifier matching the physical channel on the door controller. Auto-suggested as the next number in sequence (max + 1, or 101 if none yet). Edit if your hardware uses a different scheme."
              extra={
                channelEditing
                  ? undefined
                  : 'Auto-suggested. Edit if your install plan uses a different code.'
              }
            >
              <Input
                disabled={!!channelEditing}
                placeholder="e.g., 101"
                maxLength={20}
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
            <Form.Item
              label="Label"
              name="label"
              rules={[{ required: true, message: 'Please enter a label' }]}
            >
              <Input placeholder="e.g., Main Gate" maxLength={60} showCount />
            </Form.Item>
            <Form.Item
              label="Visitor Entry"
              name="isVisitorEntry"
              valuePropName="checked"
              tooltip="When on, visitors can trigger this door from the visitor flow."
            >
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Modal>

      {/* ----- Assign / Reassign SuperAdmin modal ----- */}
      <Modal
        title={
          saTargetSite
            ? (
              <span>
                {saTargetSite.superAdmin ? 'Reassign' : 'Assign'} SuperAdmin —{' '}
                <strong>{saTargetSite.displayName}</strong>
              </span>
            )
            : 'Assign SuperAdmin'
        }
        open={saModalOpen}
        onOk={handleSaOk}
        confirmLoading={saSaving}
        onCancel={() => {
          setSaModalOpen(false);
          saForm.resetFields();
          setSaTargetSite(null);
        }}
        okText={saTargetSite?.superAdmin ? 'Reassign + Send Invite' : 'Assign + Send Invite'}
        afterOpenChange={(open) => { if (open) populateSaForm(); }}
        destroyOnClose
        width={520}
      >
        {saTargetSite?.superAdmin && (
          <Alert
            message={`Current SuperAdmin: ${saTargetSite.superAdmin.fullname || saTargetSite.superAdmin.email}`}
            description={
              saTargetSite.superAdmin.fullname
                ? <span style={{ fontSize: 12 }}>{saTargetSite.superAdmin.email}</span>
                : null
            }
            showIcon
            style={{ marginBottom: 16 }}
            type="warning"
          />
        )}
        <Alert
          message="What this does"
          description={
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 18, fontSize: 13 }}>
              <li>Finds or creates a Firebase Auth user with this email.</li>
              <li>Grants <span className="mono">admin</span> +{' '}
                  <span className="mono">superAdmin</span> claims.</li>
              <li>Creates / updates their <span className="mono">accounts</span> record
                  with accessLevel 11.</li>
              <li>Sends a password-setup email; after they set a password
                  they&apos;re sent to{' '}
                  <span className="mono">atactconnect.web.app/signIn</span>.</li>
            </ul>
          }
          showIcon
          style={{ marginBottom: 16 }}
          type="info"
        />
        <Form form={saForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Looks malformed' },
            ]}
            tooltip="The address that will receive the password-setup invite. If a Firebase Auth user already exists for this email they'll be promoted instead of recreated."
          >
            <Input placeholder="e.g., alice@example.com" maxLength={120} />
          </Form.Item>
          <Form.Item
            label="Full Name"
            name="fullname"
            rules={[{ required: true, message: 'Please enter a full name' }]}
            tooltip="Shown in the welcome email and in the MA web sidebar."
          >
            <Input placeholder="e.g., Alice Tan" maxLength={80} />
          </Form.Item>
          <Form.Item
            label="Contact Number (optional)"
            name="contactNumber"
          >
            <Input placeholder="e.g., +65 9123 4567" maxLength={30} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ----- Delete Site modal (rev 105, ROADMAP §2.6) ----- */}
      <Modal
        title={
          delTargetSite
            ? (
              <span style={{ color: '#cf1322' }}>
                <DeleteOutlined style={{ marginRight: 8 }} />
                Delete Site — <strong>{delTargetSite.displayName}</strong>
              </span>
            )
            : 'Delete Site'
        }
        open={delModalOpen}
        onOk={handleDeleteSite}
        confirmLoading={delDeleting}
        onCancel={() => {
          setDelModalOpen(false);
          setDelTargetSite(null);
          setDelConfirmPhrase('');
        }}
        okText="Delete Site"
        okButtonProps={{
          danger: true,
          disabled: delConfirmPhrase !== delRequiredPhrase,
        }}
        destroyOnClose
        width={560}
      >
        <Alert
          message="This is destructive and cannot be undone."
          description={
            <div>
              <p style={{ marginBottom: 6 }}>Deleting this site will:</p>
              <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                <li>
                  <strong>Deactivate every account assigned to this site</strong>
                  {' '}(status → 2, assignedSite cleared). Auth users are kept;
                  accounts docs are kept for audit. They can be re-invited to
                  another site later.
                </li>
                <li>
                  <strong>Delete the site doc + every block and unit</strong>
                  {' '}beneath it.
                </li>
                <li>
                  Leave existing <code>accountRequests</code>, <code>preRegister</code>,
                  <code> onsite</code> records alone (historical). Mobile / VMS may
                  show stale rows referencing the deleted site until manually scrubbed.
                </li>
              </ul>
              <p style={{ margin: '8px 0 0' }}>
                Snapshot of the site doc lands in <code>auditLog/</code> as a{' '}
                <code>site_deleted</code> row.
              </p>
            </div>
          }
          showIcon
          type="error"
          style={{ marginBottom: 16 }}
        />

        {delTargetSite && (
          <div style={{
            background: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            padding: '8px 12px',
            fontSize: 12,
            color: '#595959',
            marginBottom: 16,
          }}>
            <div style={{ fontWeight: 600 }}>About to delete</div>
            <div style={{ fontFamily: 'monospace', marginTop: 4 }}>
              {delTargetSite.displayName} ({delTargetSite.id})<br />
              slug: {delTargetSite.slug || '(none)'}<br />
              {delTargetSite.channelCount} door channel(s) configured
            </div>
          </div>
        )}

        <div style={{ marginBottom: 8 }}>
          <strong>Type to confirm:</strong>
          <div style={{ fontFamily: 'monospace', color: '#cf1322', marginTop: 4 }}>
            {delRequiredPhrase}
          </div>
        </div>
        <Input
          placeholder="Type the phrase above exactly to enable Delete"
          value={delConfirmPhrase}
          onChange={(e) => setDelConfirmPhrase(e.target.value)}
          style={{
            fontFamily: 'monospace',
            borderColor: delConfirmPhrase === delRequiredPhrase ? '#52c41a' : undefined,
          }}
        />
      </Modal>

    </>
  );
}
